import { auth } from '../firebase';
import logger from './logger';
import api from './api';

/**
 * Log data access for HIPAA compliance
 * @param {Object} params - Access logging parameters
 * @param {string} params.collection - Collection name (e.g., 'clients')
 * @param {string} params.documentId - Document ID being accessed
 * @param {string} params.action - Action type (view, edit, export, etc.)
 * @param {Array} params.fields - Fields accessed (optional)
 * @param {string} params.reason - Reason for access (optional)
 * @returns {Promise<Object>} - Result of the logging operation
 */
export const logDataAccess = async ({ collection, documentId, action, fields = [], reason = null }) => {
  try {
    if (!auth.currentUser) {
      logger.debug('Skipping data access log - user not authenticated');
      return { success: false };
    }

    logger.debug('Logging data access', { collection, documentId, action, fields, reason });

    // Use the new access logs API endpoint
    await api.post('/api/access-logs', {
      action,
      page: `/${collection}/${documentId}`,
      collection,
      documentId,
      fields,
      reason: reason || `${action} access to ${collection}/${documentId}`,
      metadata: { collection, documentId, action, fields }
    });

    logger.info('Data access logged successfully', { collection, documentId, action });
    return { success: true };
  } catch (error) {
    logger.error('Failed to log data access', { error: error.message, collection, documentId, action });
    throw error;
  }
};

/**
 * Get audit logs (admin only)
 * @param {Object} params - Query parameters
 * @param {string} params.startDate - Start date filter (ISO string)
 * @param {string} params.endDate - End date filter (ISO string)
 * @param {string} params.userId - User ID filter
 * @param {string} params.collection - Collection filter
 * @param {string} params.documentId - Document ID filter
 * @param {string} params.action - Action filter
 * @param {number} params.limit - Maximum number of logs to return
 * @returns {Promise<Object>} - Audit logs data with { logs: [...] } format for backward compatibility
 */
export const getAuditLogs = async ({
  startDate = null,
  endDate = null,
  userId = null,
  collection = null,
  documentId = null,
  action = null,
  limit = 100
} = {}) => {
  try {
    logger.debug('Fetching audit logs', { startDate, endDate, userId, collection, documentId, action, limit });

    // Use the new access logs API endpoint
    const result = await getAccessLogs({
      startDate,
      endDate,
      userId,
      collection,
      documentId,
      action,
      limit,
      page: 1
    });

    // Transform response to match old format for backward compatibility
    const logs = result.data || [];
    logger.info('Audit logs fetched successfully', { count: logs.length });

    return {
      logs: logs.map((log) => ({
        ...log,
        // Ensure timestamp is in the expected format
        timestamp: log.timestamp || new Date()
      }))
    };
  } catch (error) {
    logger.error('Failed to fetch audit logs', { error: error.message });
    throw error;
  }
};

/**
 * Log page access for behavioral health page
 * @param {string} pageName - Name of the page being accessed
 * @param {string} action - Action type (page_view, tab_change, etc.)
 * @param {Object} additionalData - Additional data to log
 */
export const logPageAccess = async (pageName, action = 'page_view', additionalData = {}) => {
  try {
    if (!auth.currentUser) {
      logger.debug('Skipping page access log - user not authenticated');
      return;
    }

    // Use the new access logs API endpoint
    await api.post('/api/access-logs', {
      action,
      page: `/${pageName}`,
      collection: 'pages',
      documentId: pageName,
      fields: Object.keys(additionalData),
      reason: `Access to ${pageName} page`,
      metadata: additionalData
    });

    logger.userAction('page_access', { page: pageName, action, ...additionalData });
  } catch (error) {
    logger.error('Failed to log page access', { error: error.message, pageName, action });
    // Don't throw - page access logging failure shouldn't break the UI
  }
};

/**
 * Log client record access
 * @param {string} clientId - Client ID being accessed
 * @param {string} action - Action type (view, edit, export, etc.)
 * @param {Array} fields - Fields accessed
 * @param {string} reason - Reason for access
 */
export const logClientAccess = async (clientId, action = 'view', fields = [], reason = null) => {
  try {
    if (!auth.currentUser) {
      logger.debug('Skipping client access log - user not authenticated');
      return;
    }

    // Use the new access logs API endpoint
    await api.post('/api/access-logs', {
      action,
      page: `/client/${clientId}`,
      collection: 'clients',
      documentId: clientId,
      fields,
      reason: reason || `Client record ${action}`,
      metadata: { clientId, action, fields }
    });

    logger.userAction('client_access', { clientId, action, fields, reason });
  } catch (error) {
    logger.error('Failed to log client access', { error: error.message, clientId, action });
    // Don't throw - client access logging failure shouldn't break the UI
  }
};

/**
 * Log export activity
 * @param {string} exportType - Type of export (behavioral_health_clients, etc.)
 * @param {Array} clientIds - Client IDs included in export
 * @param {string} justification - User-provided justification
 * @param {string} format - Export format (csv, pdf, etc.)
 */
export const logExportActivity = async (exportType, clientIds = [], justification = '', format = 'csv') => {
  try {
    if (!auth.currentUser) {
      logger.debug('Skipping export activity log - user not authenticated');
      return;
    }

    // Use the new access logs API endpoint
    await api.post('/api/access-logs', {
      action: 'export',
      page: `/export/${exportType}`,
      collection: 'exports',
      documentId: exportType,
      fields: ['clientIds', 'justification', 'format'],
      reason: justification || `Export of ${exportType} data`,
      metadata: {
        exportType,
        clientCount: clientIds.length,
        justification,
        format
      }
    });

    logger.userAction('data_export', {
      exportType,
      clientCount: clientIds.length,
      justification,
      format
    });
  } catch (error) {
    logger.error('Failed to log export activity', { error: error.message, exportType });
    throw error; // Export logging failure should prevent export
  }
};

/**
 * Log page view using the new access logs API
 * @param {string} pagePath - Route path (e.g., '/client-list', '/client/123')
 * @param {Object} metadata - Additional metadata to include
 * @returns {Promise<void>}
 */
export const logPageView = async (pagePath, metadata = {}) => {
  try {
    if (!auth.currentUser) {
      logger.debug('Skipping page view log - user not authenticated');
      return;
    }

    // Determine collection and documentId from page path
    let collection = 'pages';
    let documentId = pagePath;

    // Extract client ID from client profile pages
    if (pagePath.startsWith('/client/')) {
      const clientId = pagePath.split('/client/')[1]?.split('?')[0];
      if (clientId) {
        collection = 'clients';
        documentId = clientId;
      }
    }











    logger.debug('Page view logged', { pagePath, collection, documentId });
  } catch (error) {
    logger.error('Failed to log page view', {
      error: error.message,
      pagePath
      // Don't throw - page view logging failure shouldn't break the UI
    });
  }
};

/**
 * Log client action using the new access logs API
 * @param {string} clientId - Client ID
 * @param {string} action - Action type (client_view, client_create, client_update, etc.)
 * @param {Array} fields - Fields accessed/modified
 * @param {string} reason - Reason for access
 * @param {Object} metadata - Additional metadata
 * @returns {Promise<void>}
 */
export const logClientAction = async (clientId, action = 'client_view', fields = [], reason = null, metadata = {}) => {
  try {
    if (!auth.currentUser) {
      logger.debug('Skipping client action log - user not authenticated');
      return;
    }











    logger.debug('Client action logged', { clientId, action, fields });
  } catch (error) {
    logger.error('Failed to log client action', {
      error: error.message,
      clientId,
      action
      // Don't throw - action logging failure shouldn't break the UI
    });
  }
};

/**
 * Get access logs from the new API endpoint
 * @param {Object} filters - Filter parameters
 * @param {string} filters.startDate - Start date (ISO string or YYYY-MM-DD)
 * @param {string} filters.endDate - End date (ISO string or YYYY-MM-DD)
 * @param {string} filters.userId - User ID filter
 * @param {string} filters.pagePath - Page/route path filter
 * @param {string} filters.action - Action type filter
 * @param {string} filters.collection - Collection filter
 * @param {string} filters.documentId - Document ID filter
 * @param {number} filters.page - Page number (default: 1)
 * @param {number} filters.limit - Items per page (default: 50, max: 100)
 * @returns {Promise<Object>} - Access logs with pagination
 */
export const getAccessLogs = async (filters = {}) => {
  try {
    const {
      startDate = null,
      endDate = null,
      userId = null,
      pagePath = null,
      action = null,
      collection = null,
      documentId = null,
      page = 1,
      limit = 50
    } = filters;

    // Build query string
    const queryParams = new URLSearchParams();
    if (startDate) {
      queryParams.append('startDate', startDate);
    }
    if (endDate) {
      queryParams.append('endDate', endDate);
    }
    if (userId) {
      queryParams.append('userId', userId);
    }
    if (pagePath) {
      queryParams.append('pagePath', pagePath);
    }
    if (action) {
      queryParams.append('action', action);
    }
    if (collection) {
      queryParams.append('collection', collection);
    }
    if (documentId) {
      queryParams.append('documentId', documentId);
    }
    queryParams.append('page', page.toString());
    queryParams.append('limit', Math.min(100, Math.max(1, limit)).toString());

    const queryString = queryParams.toString();
    const url = `/api/access-logs${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);

    logger.debug('Access logs retrieved', {
      count: response.data?.data?.length || 0,
      filters
    });

    return response.data;
  } catch (error) {
    logger.error('Failed to fetch access logs', { error: error.message, filters });
    throw error;
  }
};
